/**
 * Turning a thrown thing into a sentence.
 *
 * Nakama's client rejects with plain objects as often as with Errors, and its
 * messages are written for developers. So a caller supplies the sentence a
 * player should see, and the underlying text is used only when it is likely to
 * help — never a bare status code or a stack.
 */

export function describeError(caught: unknown, fallback: string): string {
  if (typeof caught === 'object' && caught !== null) {
    const body = caught as { message?: unknown; status?: unknown };

    // 4xx means we sent something wrong and the caller's sentence explains it
    // better than the server's does.
    if (typeof body.status === 'number' && body.status >= 400 && body.status < 500) {
      return fallback;
    }
    if (typeof body.message === 'string' && body.message.length > 0 && body.message.length < 120) {
      return body.message;
    }
  }
  if (caught instanceof Error && caught.message.length < 120) return caught.message;
  return fallback;
}

/**
 * What to tell a player when the server refuses something.
 *
 * The protocol says this plainly and the client was not listening:
 *
 * > Messages are for developers and log lines. Clients switch on `code`,
 * > never on the text.
 *
 * The banner printed `message` directly, so a player who hit a protocol
 * hiccup was shown "unsupported opcode", "roster moved on" or "bad sync
 * request" in red. Several of those are not even about anything they did.
 *
 * Unknown codes still fall through to the server's text. That case is a client
 * older or newer than the server it is talking to - a developer's problem, and
 * the developer is the one who wants the original words.
 */
export function describeMatchError(error: { code: string; message?: string }): string {
  switch (error.code) {
    // A race, and the right response is simply to try again: somebody else's
    // move landed between this screen rendering and the tap arriving.
    case 'STALE':
      return 'Somebody moved first — try again.';
    case 'OUT_OF_TURN':
      return "It is not your turn yet.";
    case 'ILLEGAL_MOVE':
      return 'That is not a legal play.';
    case 'UNKNOWN_MOVE':
      return 'That is not part of this game.';
    case 'NOT_HOST':
      return 'Only the host can do that.';
    case 'NOT_SEATED':
      return 'You are watching this table, not playing at it.';
    case 'PAUSED':
      return 'The host has paused the game.';
    case 'GAME_OVER':
      return 'This game has already finished.';
    case 'RATE_LIMITED':
      return 'Slow down a moment.';
    case 'CHAT_DISABLED':
      return 'The host has turned typing off.';
    case 'VOICE_DISABLED':
      return 'Voice is off at this table.';
    // The room is paused rather than gone, so the useful half is what happens
    // next rather than what broke.
    case 'INTERNAL':
      return 'Something went wrong here. The room has been paused.';
    /*
     * Nothing a player did, and nothing they can do. The specific text -
     * "unsupported opcode", "bad sync request" - describes a conversation
     * between two programs.
     */
    case 'MALFORMED':
      return 'Something went wrong sending that.';
    default:
      return error.message && error.message.length > 0 ? error.message : 'Something went wrong.';
  }
}
